import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { schoolsAPI, campusAPI } from "../../api";
import { School, Campus } from "../../types";
import { useSchool } from "../../contexts/SchoolContext";
import { useCampus } from "../../contexts/CampusContext";
import { handleSchoolId } from "../../utils/helpers";
import { SearchableSelect } from "./SearchableSelect";
import { GraduationCap, Building2 } from "lucide-react";

/**
 * Global school + campus scope selector rendered in the dashboard header.
 * Defaults to "All Schools" / "All Campuses" (no filter = show all data).
 * Selecting a school filters the campus dropdown to that school's campuses.
 * Each dropdown has a search box, so it scales to hundreds/thousands of schools.
 * Selection is held in SchoolContext/CampusContext and persisted to localStorage.
 */
export function GlobalScopeFilter() {
  const { selectedSchool, setSelectedSchool, setAvailableSchools } = useSchool();
  const { selectedCampus, setSelectedCampus, setAvailableCampuses } =
    useCampus();

  const { data: schoolsData, isLoading: schoolsLoading } = useQuery({
    queryKey: ["schools-filter"],
    queryFn: schoolsAPI.getAllSchools,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const { data: campusesData, isLoading: campusesLoading } = useQuery({
    queryKey: ["campus"],
    queryFn: campusAPI.getAllCampuses,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const schools: School[] = useMemo(
    () => (Array.isArray(schoolsData) ? schoolsData : []),
    [schoolsData]
  );

  const allCampuses: Campus[] = useMemo(() => {
    if (Array.isArray(campusesData)) return campusesData;
    if (campusesData?.data && Array.isArray(campusesData.data))
      return campusesData.data;
    return [];
  }, [campusesData]);

  // Campuses belonging to the currently selected school (empty = "All Schools").
  const campuses = useMemo(() => {
    if (!selectedSchool) return [];
    return allCampuses.filter(
      (campus) => handleSchoolId(campus.school_id) === selectedSchool._id
    );
  }, [allCampuses, selectedSchool]);

  const schoolOptions = useMemo(
    () =>
      schools.map((school) => ({
        value: school._id,
        label: school.display_name || school.school_name,
      })),
    [schools]
  );

  const campusOptions = useMemo(
    () =>
      campuses.map((campus) => ({
        value: campus._id,
        label: campus.campus_name || campus.name || "Unnamed campus",
      })),
    [campuses]
  );

  // Keep the contexts' available lists in sync for any other consumers.
  useEffect(() => {
    setAvailableSchools(schools);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolsData]);

  useEffect(() => {
    setAvailableCampuses(campuses);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campuses]);

  const handleSchoolChange = (value: string) => {
    if (value === "all") {
      setSelectedSchool(null);
      setSelectedCampus(null);
      return;
    }
    setSelectedSchool(schools.find((s) => s._id === value) || null);
    // Reset campus whenever the school changes.
    setSelectedCampus(null);
  };

  const handleCampusChange = (value: string) => {
    if (value === "all") {
      setSelectedCampus(null);
      return;
    }
    setSelectedCampus(campuses.find((c) => c._id === value) || null);
  };

  return (
    <div className="flex items-center gap-2">
      <SearchableSelect
        value={selectedSchool?._id || "all"}
        onValueChange={handleSchoolChange}
        options={schoolOptions}
        allLabel="All Schools"
        placeholder="All Schools"
        searchPlaceholder="Search schools..."
        emptyText="No schools found."
        disabled={schoolsLoading}
        icon={<GraduationCap className="h-4 w-4 shrink-0 opacity-60" />}
        className="w-[150px] sm:w-[190px]"
      />

      <SearchableSelect
        value={selectedCampus?._id || "all"}
        onValueChange={handleCampusChange}
        options={campusOptions}
        allLabel="All Campuses"
        placeholder="All Campuses"
        searchPlaceholder="Search campuses..."
        emptyText="No campuses found."
        disabled={!selectedSchool || campusesLoading}
        icon={<Building2 className="h-4 w-4 shrink-0 opacity-60" />}
        className="w-[150px] sm:w-[190px]"
      />
    </div>
  );
}
